import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * Gratis-Muster-Fenster: listet die Muster aus dem Warenkorb, erlaubt das
 * Entfernen und führt zur Bestellung. Öffnen per Event:
 *   document.dispatchEvent(new CustomEvent('plixxo-muster:open', { detail: { state: 'added' } }))
 * state: 'added' | 'limit' | undefined (nur Übersicht)
 *
 * Markup contract: snippets/plixxo-muster-sheet.liquid
 */
const OPEN_EVENT = 'plixxo-muster:open';

const CLOSE_ICON =
  '<svg viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M1 1l12 12M13 1L1 13" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';

class PlixxoMusterSheet extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;
  /** @type {any[]} */
  #lines = [];
  #otherItems = 0;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    // Aus Containern mit transform/overflow lösen, damit das Fenster immer fixiert bleibt.
    if (this.parentElement !== document.body) document.body.appendChild(this);

    document.addEventListener(OPEN_EVENT, this.#handleOpen, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, { signal });

    const dialog = this.dialog;
    dialog?.addEventListener('click', this.#handleDialogClick, { signal });
    dialog?.addEventListener('close', () => document.documentElement.classList.remove('pm-sheet-open'), { signal });
    this.querySelector('[data-pm-list]')?.addEventListener('click', this.#handleRemove, { signal });
    this.querySelector('[data-pm-checkout]')?.addEventListener('click', this.#handleCheckout, { signal });
    this.#bindSwipe(signal);
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  get dialog() {
    return /** @type {HTMLDialogElement | null} */ (this.querySelector('dialog'));
  }

  get limit() {
    return Number(this.dataset.limit) || 6;
  }

  /** @param {Event & {detail?: any}} event */
  #handleOpen = async (event) => {
    const state = event.detail?.state;
    this.#setNotice(state);
    await this.#refresh();
    const dialog = this.dialog;
    if (dialog && !dialog.open) {
      dialog.showModal();
      document.documentElement.classList.add('pm-sheet-open');
      /** @type {HTMLElement | null} */ (dialog.querySelector('[data-pm-close]'))?.focus({ preventScroll: true });
    }
  };

  /** @param {Event & {detail?: any}} event */
  #handleCartUpdate = (event) => {
    if (event.detail?.data?.source === 'plixxo-muster-sheet') return;
    if (this.dialog?.open) this.#refresh();
  };

  /** @param {MouseEvent} event */
  #handleDialogClick = (event) => {
    const target = /** @type {Element} */ (event.target);
    if (target === this.dialog || target.closest('[data-pm-close]')) this.close();
  };

  close() {
    this.dialog?.close();
  }

  /** @param {string | undefined} state */
  #setNotice(state) {
    const notice = this.querySelector('[data-pm-notice]');
    const headline = this.querySelector('[data-pm-headline]');
    if (!notice || !headline) return;
    const text = state === 'added' ? this.dataset.textAdded : state === 'limit' ? this.dataset.textLimit : '';
    notice.toggleAttribute('hidden', !text);
    notice.classList.toggle('pm-sheet__notice--alert', state === 'limit');
    headline.textContent = text || '';
  }

  async #refresh() {
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
        signal: this.#controller?.signal,
      });
      const cart = await response.json();
      this.#lines = cart.items.filter(
        (/** @type {any} */ item) => String(item.product_id) === this.dataset.musterProductId
      );
      this.#otherItems = cart.items.length - this.#lines.length;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error(error);
    }
    this.#render();
  }

  #render() {
    const count = this.#lines.reduce((sum, item) => sum + item.quantity, 0);

    const countEl = this.querySelector('[data-pm-count]');
    if (countEl) countEl.textContent = (this.dataset.textCount || '').replace('[count]', String(count));

    this.querySelector('[data-pm-empty]')?.toggleAttribute('hidden', count > 0);

    const list = this.querySelector('[data-pm-list]');
    if (list) {
      list.replaceChildren(...this.#lines.map((item) => this.#renderItem(item)));
    }

    const checkout = /** @type {HTMLAnchorElement | null} */ (this.querySelector('[data-pm-checkout]'));
    if (checkout) {
      checkout.toggleAttribute('hidden', count === 0);
      // Nur Muster im Warenkorb → direkt gratis bestellen, sonst erst in den Warenkorb.
      const onlySamples = this.#otherItems === 0;
      checkout.href = onlySamples ? this.dataset.checkoutUrl || '/checkout' : this.dataset.cartUrl || '/cart';
      checkout.dataset.mode = onlySamples ? 'checkout' : 'cart';
      checkout.textContent = (onlySamples ? this.dataset.textCheckout : this.dataset.textToCart) || '';
    }
  }

  /** @param {any} item */
  #renderItem(item) {
    const [fabric, ...rest] = String(item.variant_title || '').split(' / ');
    const color = rest.join(' / ');
    const name = color ? (this.dataset.textSample || '[name]').replace('[name]', fabric) : item.product_title;

    const li = document.createElement('li');
    li.className = 'pm-sheet__item';
    li.dataset.key = item.key;

    const img = document.createElement('img');
    img.alt = '';
    img.width = 88;
    img.height = 88;
    img.loading = 'lazy';
    if (item.image) img.src = this.#sizedImage(item.image, 200);

    const text = document.createElement('div');
    text.className = 'pm-sheet__item-text';
    const title = document.createElement('p');
    title.className = 'pm-sheet__item-title';
    title.textContent = name;
    const variant = document.createElement('p');
    variant.className = 'pm-sheet__item-variant';
    variant.textContent = color || fabric;
    text.append(title, variant);

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'button-unstyled pm-sheet__remove';
    remove.dataset.pmRemove = item.key;
    remove.setAttribute('aria-label', (this.dataset.textRemove || '').replace('[name]', `${name} ${color}`.trim()));
    remove.innerHTML = CLOSE_ICON;

    li.append(img, text, remove);
    return li;
  }

  /**
   * @param {string} url
   * @param {number} width
   */
  #sizedImage(url, width) {
    try {
      const parsed = new URL(url, location.origin);
      parsed.searchParams.set('width', String(width));
      return parsed.toString();
    } catch {
      return url;
    }
  }

  /** @param {MouseEvent} event */
  #handleRemove = async (event) => {
    const button = /** @type {Element} */ (event.target).closest('[data-pm-remove]');
    if (!(button instanceof HTMLElement)) return;
    const key = button.dataset.pmRemove;
    const row = button.closest('.pm-sheet__item');
    row?.setAttribute('aria-busy', 'true');

    try {
      const sectionIds = [...document.querySelectorAll('cart-items-component')]
        .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
        .filter(Boolean);

      const response = await fetch(Theme.routes.cart_change_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0, sections: sectionIds.join(',') }),
        signal: this.#controller?.signal,
      });
      const cart = await response.json();
      if (!response.ok) throw new Error(cart.description || 'cart change failed');

      // Horizon öffnet den Warenkorb-Drawer bei jedem cart:update (auto-open) – hier nicht gewollt.
      const drawer = document.querySelector('cart-drawer-component[auto-open]');
      drawer?.removeAttribute('auto-open');
      document.dispatchEvent(
        new CartUpdateEvent({}, 'plixxo-muster-sheet', {
          source: 'plixxo-muster-sheet',
          itemCount: cart.item_count,
          sections: cart.sections,
        })
      );
      drawer?.setAttribute('auto-open', '');
      this.#setNotice(undefined);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error(error);
    }
    await this.#refresh();
  };

  /** @param {MouseEvent} event */
  #handleCheckout = (event) => {
    const link = /** @type {HTMLAnchorElement} */ (event.currentTarget);
    if (link.dataset.mode !== 'cart') return;
    const drawer = /** @type {any} */ (document.querySelector('cart-drawer-component'));
    if (!drawer?.open) return;
    event.preventDefault();
    this.close();
    drawer.open();
  };

  /** Mobil: Fenster am Griff/Kopf nach unten wischen schließt es. */
  /** @param {AbortSignal} signal */
  #bindSwipe(signal) {
    const dialog = this.dialog;
    const handle = this.querySelector('.pm-sheet__bar');
    if (!dialog || !handle) return;
    let startY = 0;
    let delta = 0;

    const zones = [handle, this.querySelector('.pm-sheet__grip')].filter(Boolean);
    zones.forEach((zone) => {
      zone?.addEventListener(
        'touchstart',
        (event) => {
          startY = /** @type {TouchEvent} */ (event).touches[0].clientY;
          delta = 0;
          dialog.style.transition = 'none';
        },
        { passive: true, signal }
      );
      zone?.addEventListener(
        'touchmove',
        (event) => {
          delta = Math.max(0, /** @type {TouchEvent} */ (event).touches[0].clientY - startY);
          dialog.style.transform = `translateY(${delta}px)`;
        },
        { passive: true, signal }
      );
      zone?.addEventListener(
        'touchend',
        () => {
          dialog.style.transition = '';
          dialog.style.transform = '';
          if (delta > 90) this.close();
        },
        { signal }
      );
    });
  }
}

if (!customElements.get('plixxo-muster-sheet')) {
  customElements.define('plixxo-muster-sheet', PlixxoMusterSheet);
}
